/*
 * Layout  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FunctionComponent, useCallback, useMemo, useRef } from "react";
import { BEM } from "@adapptio/lib-components";
import { isFunction, isString } from "@adapptio/lib-shared";
import { LayoutState_Column, LayoutState_Row } from "./LayoutState";
import { LayoutContextProvider } from "./LayoutContext";
import { LAYOUT_TYPE, TLayoutActivateHandler, TLayoutRenderComponentFn } from "./LayoutTypes";
import { useLayoutElement } from "./useLayoutElement";
import { renderLayoutElement } from "./renderLayoutElement";
import { Resize } from "../Resize/Resize";
import { LayoutResizableElement } from "./LayoutResizableElement";
import { RESIZE_DIRECTION } from "../Resize/ResizeTypes";

const getCssTypeModifier = (type: LAYOUT_TYPE) =>
	({
		[LAYOUT_TYPE.COLUMN]: "column",
		[LAYOUT_TYPE.ROW]: "row"
	}[type] || null);

export interface ILayoutContainerProps {
	state: LayoutState_Column | LayoutState_Row;
	renderComponent: TLayoutRenderComponentFn;
	onActivate?: TLayoutActivateHandler;
	className?: string;
}

export const LayoutContainer: FunctionComponent<ILayoutContainerProps> = ({
	state,
	renderComponent,
	onActivate,
	className
}) => {
	const el = useLayoutElement(state);
	const layoutElRef = useRef<HTMLDivElement>();
	const isExpanded = !el.isExpandable || (el.isExpandable && el.isExpanded);
	const resizeDirection = useMemo(
		() =>
			({
				[LAYOUT_TYPE.COLUMN]: RESIZE_DIRECTION.VERTICAL,
				[LAYOUT_TYPE.ROW]: RESIZE_DIRECTION.HORIZONTAL
			}[state.type]),
		[ state.type ]
	);

	const style: React.CSSProperties = {};

	if (isString(el.size)) {
		if (state.type === LAYOUT_TYPE.COLUMN) {
			style.width = el.size;
		}

		if (state.type === LAYOUT_TYPE.ROW) {
			style.height = el.size;
		}
	} else {
		style.flex = `${el.size} 1 0%`;
	}

	const handleFocus = useCallback(() => {
		if (isFunction(onActivate)) {
			onActivate(el);
		}
	}, [ onActivate ]);

	const baseClass = BEM("edt-layout", "element", {
		type: getCssTypeModifier(state.type),
		"is-expandable": el.isExpandable,
		"is-expanded": el.isExpandable && el.isExpanded,
		"is-collapsed": el.isExpandable && !el.isExpanded
	});

	baseClass.add("m-layout-cnt-" + el.id);

	if (className) {
		baseClass.add(className);
	}

	return (
		<LayoutResizableElement
			state={state}
			className={baseClass.toClassName()}
			style={style}
			onFocus={handleFocus}
			minSize={200}
			ref={layoutElRef}
		>
			<LayoutContextProvider state={state} layoutElRef={layoutElRef}>
				<Resize direction={resizeDirection}>
					{isExpanded || (!isExpanded && el.renderWhenCollapsed !== false)
						? el.children.map((child) => renderLayoutElement(child, renderComponent, onActivate))
						: null}
				</Resize>
			</LayoutContextProvider>
		</LayoutResizableElement>
	);
};
